/**
 * Website runtime configuration loader.
 *
 * Loads /config.json from the website root and returns the UUID/Jokes function URLs if present.
 * Emits logs via loglevel for diagnostics.
 */

import { logger } from "./logger.js";

export type ConfigurableWelcomeNba = {
  id: string;
  text: string;
};

const DEFAULT_WELCOME_MESSAGES = [
  "This is the personal website of Jonas Taulien.",
  "This Website works a little bit different, than you are used to: Its an Agentic UI.\n\nAsk for what you need and it will present it to you.",
  "You can also start with one of these suggestions:",
];

const DEFAULT_WELCOME_NBAS: ConfigurableWelcomeNba[] = [
  { id: "MOST_RECENT_PROJECT_VARIANT_1", text: "What was Jonas latest project as a freelancer?" },
  { id: "ANY_TESTIMONIAL_VARIANT_1", text: "Show my the most recent customer reference" },
];

const DEFAULT_WELCOME_MESSAGE_DELAY_MS = 2000;

export type SiteConfig = {
  "uuid-function-url"?: string;
  "jokes-function-url"?: string;
  "welcome-messages"?: unknown;
  "default-welcome-nbas"?: unknown;
  "welcome-message-delay-ms"?: unknown;
};

export type LoadedConfig = {
  uuidFunctionUrl: string | null;
  jokesFunctionUrl: string | null;
  welcomeMessages: string[];
  defaultWelcomeNbas: ConfigurableWelcomeNba[];
  welcomeMessageDelayMs: number;
};

function sanitizeWelcomeMessages(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [...DEFAULT_WELCOME_MESSAGES];
  }

  const cleaned = raw
    .filter((value): value is string => typeof value === "string")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return cleaned.length > 0 ? cleaned : [...DEFAULT_WELCOME_MESSAGES];
}

function sanitizeDefaultWelcomeNbas(raw: unknown): ConfigurableWelcomeNba[] {
  if (!Array.isArray(raw)) {
    return [...DEFAULT_WELCOME_NBAS];
  }

  const nbas: ConfigurableWelcomeNba[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const record = item as Record<string, unknown>;
    const id = typeof record.id === "string" ? record.id.trim() : "";
    const text = typeof record.text === "string" ? record.text.trim() : "";
    if (!id || !text) {
      continue;
    }
    nbas.push({ id, text });
  }

  return nbas.length > 0 ? nbas : [...DEFAULT_WELCOME_NBAS];
}

function sanitizeWelcomeMessageDelay(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw < 0) {
    return DEFAULT_WELCOME_MESSAGE_DELAY_MS;
  }
  return raw;
}

/**
 * Load configuration from /config.json.
 *
 * The loader accepts a fetch-like function for easier testing (dependency injection).
 */
export async function loadConfig(fetchFn: typeof fetch = fetch): Promise<LoadedConfig> {
  try {
    logger.info("Loading site config", { path: "/config.json" });
    const res = await fetchFn("/config.json", { method: "GET" });
    if (!res.ok) {
      logger.warn("Config fetch failed", { status: res.status, statusText: res.statusText });
      return {
        uuidFunctionUrl: null,
        jokesFunctionUrl: null,
        welcomeMessages: [...DEFAULT_WELCOME_MESSAGES],
        defaultWelcomeNbas: [...DEFAULT_WELCOME_NBAS],
        welcomeMessageDelayMs: DEFAULT_WELCOME_MESSAGE_DELAY_MS,
      };
    }
    const raw = (await res.json()) as unknown;
    const obj: Record<string, unknown> = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const uuidRaw = obj["uuid-function-url"];
    const jokesRaw = obj["jokes-function-url"];
    const welcomeMessages = sanitizeWelcomeMessages(obj["welcome-messages"]);
    const defaultWelcomeNbas = sanitizeDefaultWelcomeNbas(obj["default-welcome-nbas"]);
    const welcomeMessageDelayMs = sanitizeWelcomeMessageDelay(obj["welcome-message-delay-ms"]);
    const uuidUrl = typeof uuidRaw === "string" ? uuidRaw.trim() : "";
    const jokesUrl = typeof jokesRaw === "string" ? jokesRaw.trim() : "";
    const loaded = {
      uuidFunctionUrl: uuidUrl || null,
      jokesFunctionUrl: jokesUrl || null,
      welcomeMessages,
      defaultWelcomeNbas,
      welcomeMessageDelayMs,
    } satisfies LoadedConfig;

    if (!uuidUrl) {
      logger.info("No uuid-function-url configured");
    }
    if (!jokesUrl) {
      logger.info("No jokes-function-url configured");
    }
    logger.info("Config loaded", {
      hasUuidUrl: !!uuidUrl,
      hasJokesUrl: !!jokesUrl,
      welcomeMessagesCount: welcomeMessages.length,
      defaultWelcomeNbaCount: defaultWelcomeNbas.length,
      welcomeMessageDelayMs,
    });
    return loaded;
  } catch (err) {
    logger.error("Config load error", { error: String(err) });
    return {
      uuidFunctionUrl: null,
      jokesFunctionUrl: null,
      welcomeMessages: [...DEFAULT_WELCOME_MESSAGES],
      defaultWelcomeNbas: [...DEFAULT_WELCOME_NBAS],
      welcomeMessageDelayMs: DEFAULT_WELCOME_MESSAGE_DELAY_MS,
    };
  }
}
